import React, { FC } from "react";

import { FormCardInput } from "stentor-models";

import { Styleable } from "../../types";
import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldCard.module.scss";

export interface FormCardProps extends FormFieldProps {
    readonly field: Styleable<FormCardInput>;
}

/**
 * The element each text variant renders as -- MUI Typography's default variant mapping, which
 * this card used until #1741. The variant's look comes from FormFieldCard.module.scss.
 */
const VARIANT_ELEMENTS: Record<string, "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span"> = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    subtitle1: "h6",
    subtitle2: "h6",
    body1: "p",
    body2: "p",
    inherit: "p",
    button: "span",
    caption: "span",
    overline: "span",
};

const DEFAULT_VARIANT = "body2";

/**
 * Typography's palette shorthands, at the default MUI theme's values. Anything else is taken
 * to be a CSS colour and passed straight through, as Typography did.
 */
const PALETTE_COLORS: Record<string, string> = {
    primary: "#1976d2",
    secondary: "#9c27b0",
    error: "#d32f2f",
    warning: "#ed6c02",
    info: "#0288d1",
    success: "#2e7d32",
    textPrimary: "rgba(0, 0, 0, 0.87)",
    textSecondary: "rgba(0, 0, 0, 0.6)",
    textDisabled: "rgba(0, 0, 0, 0.38)",
    "text.primary": "rgba(0, 0, 0, 0.87)",
    "text.secondary": "rgba(0, 0, 0, 0.6)",
    "text.disabled": "rgba(0, 0, 0, 0.38)",
};

const DEFAULT_COLOR = "textSecondary";

function resolveColor(color: string): string {
    return PALETTE_COLORS[color] || color;
}

export const FormFieldCard: FC<FormCardProps> = (props) => {
    const { field } = props;

    const variant = field.variant && VARIANT_ELEMENTS[field.variant] ? field.variant : DEFAULT_VARIANT;
    const Text = VARIANT_ELEMENTS[variant];

    return (
        <div className={styles.xapp_form_card}>
            <div className={styles.xapp_form_card_body}>
                {field.header && (
                    <div className={styles.xapp_form_card_header}>
                        <span className={styles.xapp_form_card_header_title}>{field.header.title}</span>
                        {field.header.subheader && (
                            <span className={styles.xapp_form_card_header_subheader}>{field.header.subheader}</span>
                        )}
                    </div>
                )}

                {field.media && (
                    <img
                        className={styles.xapp_form_card_media}
                        src={field.media.imageUrl}
                        alt={field.media.alt}
                        height={field.media.height}
                        width={field.media.width}
                    />
                )}

                {field.text && (
                    <div className={styles.xapp_form_card_content}>
                        <Text
                            className={`${styles.xapp_form_card_text} ${styles[`xapp_form_card_text_${variant}`] || ""}`}
                            data-variant={variant}
                            style={{
                                whiteSpace: "pre-line",
                                textAlign: (field.align || "center") as React.CSSProperties["textAlign"],
                                color: resolveColor(field.color || DEFAULT_COLOR),
                                ...field.style,
                            }}>
                            {field.text}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormFieldCard;
